import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const STORAGE_KEY = "truth-scan-hub-fast-check";
const COLLAPSED_KEY = "truth-scan-hub-fast-check-collapsed";

const FastCheckContext = createContext({
  results: null,
  isCollapsed: false,
  setResults: () => {},
  collapse: () => {},
  expand: () => {},
  clear: () => {},
});

const readJSON = (key, fallback) => {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeJSON = (key, value) => {
  if (typeof window === "undefined") return;
  try {
    if (value === null || value === undefined) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, JSON.stringify(value));
    }
  } catch {
    /* storage blocked */
  }
};

export const FastCheckProvider = ({ children }) => {
  const [results, setResultsState] = useState(() => readJSON(STORAGE_KEY, null));
  const [isCollapsed, setIsCollapsed] = useState(() => readJSON(COLLAPSED_KEY, false));

  useEffect(() => {
    writeJSON(STORAGE_KEY, results);
  }, [results]);

  useEffect(() => {
    writeJSON(COLLAPSED_KEY, isCollapsed);
  }, [isCollapsed]);

  const setResults = useCallback((data) => {
    setResultsState(data);
    // Show the panel expanded on any new lookup.
    if (data) setIsCollapsed(false);
  }, []);

  const collapse = useCallback(() => setIsCollapsed(true), []);
  const expand = useCallback(() => setIsCollapsed(false), []);
  const clear = useCallback(() => {
    setResultsState(null);
    setIsCollapsed(false);
  }, []);

  const value = useMemo(
    () => ({ results, isCollapsed, setResults, collapse, expand, clear }),
    [results, isCollapsed, setResults, collapse, expand, clear]
  );

  return (
    <FastCheckContext.Provider value={value}>
      {children}
    </FastCheckContext.Provider>
  );
};

export const useFastCheck = () => useContext(FastCheckContext);

export default FastCheckContext;
